import React, { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';
import { system } from 'styled-system';
import { avatarImageSet, generateRandomInclusive } from './AvatarSet';
import { CloudImage } from '@components/CloudImage';
import { JoykitColor } from '@components/themes';
import { Text } from '@components/typography';
import { avatarData } from '@data-ts/components/Avatar';
import { themeColor, themeMediaQuery } from '@utils/styledTheme.util';

export const AvatarSizes = {
  xs: '32px',
  sm: '48px',
  md: '96px',
  lg: '148px',
  xl: '256px',
} as const;

export type SizeVariant = typeof AvatarSizes[keyof typeof AvatarSizes] | '56px' | string;

const avatarSizesScale: Array<SizeVariant> = ['32px', '48px', '96px', '148px', '256px'];

export type AvatarProps = Readonly<{
  sizeVariant?: SizeVariant | SizeVariant[];
  src?: string; // Use passed in value if non null, choose random from AvatarSourceSet otherwise.
  strokeWidth: string; // 25px, etc.
  labelColor?: JoykitColor; // rgba color being used here.
  strokeColor?: JoykitColor;
  label?: string; // Avatar name label
  style?: CSSProperties; // Allow additional styles to be passed in.
  className?: string;
  resSizeVariant?: SizeVariant | SizeVariant[];
}>;

const StyledAvatar = styled(CloudImage)
  .withConfig({
    shouldForwardProp: (prop, defaultValidatorFn) => {
      return (
        !['sizeVariant'].includes(prop) && !['strokeWidth'].includes(prop) && !['strokeColor'].includes(prop) && defaultValidatorFn(prop)
      );
    },
  })
  .attrs(({ sizeVariant, src, strokeWidth, strokeColor, resSizeVariant }: AvatarProps) => ({
    sizeVariant,
    src,
    strokeWidth,
    strokeColor,
    resSizeVariant,
  }))`
  ${system({
    sizeVariant: { properties: ['width', 'height'], defaultScale: avatarSizesScale },
  })};
  border-radius: 50%;
  border: solid;
  border-color: ${props => props.strokeColor && themeColor(props.strokeColor)};
  border-width: ${props => props.strokeWidth};
  ${themeMediaQuery('below_sm')}{
    ${system({
      resSizeVariant: { properties: ['width', 'height'], defaultScale: avatarSizesScale },
    })};
  }
`;

const StyledLabelContainer = styled.div<{ backgroundColor: string }>`
  margin-top: -15px;
  background-color: ${props => props.backgroundColor};
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  p {
    margin-bottom: 0px !important;
  }
`;

export const getRandomAvatarUrl = () => {
  // NOTE: To account for jagged sets (i.e. multiple sets of images of different lengths)
  // we will do the following:
  // 1. Randomly pick an array to choose from.
  const imageSetIndex = generateRandomInclusive(0, avatarImageSet.length - 1);
  // 2. Randomly choose an index in the chosen array and we have our image source.
  const imageIndex = generateRandomInclusive(0, avatarImageSet[imageSetIndex].length - 1);

  return avatarImageSet[imageSetIndex][imageIndex];
};

export const Avatar: React.FC<AvatarProps> = props => {
  const randomizedImageURL = useMemo(getRandomAvatarUrl, []);
  return (
    <div style={{ ...props.style }} className={props.className}>
      <StyledAvatar
        sizeVariant={props.sizeVariant}
        resSizeVariant={props.resSizeVariant}
        src={props.src ? props.src : randomizedImageURL}
        strokeWidth={props.strokeWidth || '8px'}
        strokeColor={props.strokeColor || 'yellow3'}
        alt={avatarData.altText}
      />
      {props.label && (
        <StyledLabelContainer backgroundColor={props.strokeColor || 'yellow3'}>
          <Text variant="dek20" color={props.labelColor} tagName="p">
            {props.label}
          </Text>
        </StyledLabelContainer>
      )}
    </div>
  );
};
